/**
 * Mordeczka.eu Admin - Series List Page
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Table,
  Button,
  Space,
  Typography,
  Card,
  Popconfirm,
  message,
  Input,
  Row,
  Col,
  Image as AntImage,
  Modal,
  Tooltip,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CloseOutlined,
  FolderAddOutlined,
  AppstoreAddOutlined,
} from "@ant-design/icons";
import { Tv } from "lucide-react";
import type { ColumnsType } from "antd/es/table";
import { worksApi, imagesApi } from "../services/api";
import { usePermissions } from "../hooks/usePermissions";
import type { Work } from "../types";

const { Title } = Typography;
const { Search } = Input;

export function SeriesListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { canDelete } = usePermissions();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 100 });
  const [sorter, setSorter] = useState<{
    field?: string;
    order?: "asc" | "desc";
  }>({ field: "created_at", order: "desc" });
  const [search, setSearch] = useState("");

  // Preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["works", "series", pagination, search, sorter],
    queryFn: () =>
      worksApi.list({
        page: pagination.current,
        page_size: pagination.pageSize,
        search: search || undefined,
        type: "series",
        sort: sorter.field,
        order: sorter.order,
      }),
  });

  const handleTableChange = (
    newPagination: any,
    _filters: any,
    newSorter: any,
  ) => {
    setPagination(newPagination);
    if (newSorter.field) {
      setSorter({
        field: newSorter.field as string,
        order:
          newSorter.order === "ascend"
            ? "asc"
            : newSorter.order === "descend"
              ? "desc"
              : undefined,
      });
    }
  };

  const deleteMutation = useMutation({
    mutationFn: worksApi.delete,
    onSuccess: () => {
      message.success("Serial usunięty pomyślnie");
      queryClient.invalidateQueries({ queryKey: ["works"] });
    },
    onError: () => message.error("Nie udało się usunąć"),
  });

  const openPreview = (url: string) => {
    setPreviewUrl(url);
    setPreviewOpen(true);
  };

  const closePreview = () => {
    setPreviewOpen(false);
  };

  const columns: ColumnsType<Work> = [
    {
      title: "Data",
      dataIndex: "created_at",
      key: "created_at",
      width: 120,
      sorter: true,
      render: (date: string) => new Date(date).toLocaleDateString("pl-PL"),
    },
    {
      title: "",
      dataIndex: "cover_image",
      key: "cover_image",
      width: 60,
      render: (coverImage: { id: string } | null) =>
        coverImage ? (
          <AntImage
            src={imagesApi.getFileUrl(coverImage.id)}
            alt="Okładka"
            width={40}
            style={{
              objectFit: "cover",
              borderRadius: 4,
              aspectRatio: "2/3",
              cursor: "pointer",
            }}
            preview={false}
            onClick={() => openPreview(imagesApi.getFileUrl(coverImage.id))}
          />
        ) : (
          <div
            style={{
              width: 40,
              aspectRatio: "2/3",
              background: "#f5f5f5",
              borderRadius: 4,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Tv size={16} style={{ color: "#d9d9d9" }} />
          </div>
        ),
    },
    {
      title: "Tytuł",
      dataIndex: "title",
      key: "title",
      sorter: true,
      ellipsis: true,
      render: (text: string) => <strong>{text}</strong>,
    },
    {
      title: "Lata",
      key: "years",
      width: 130,
      align: "center",
      responsive: ["md"],
      render: (_: unknown, record: Work) => {
        const seasons = record.series_details?.seasons || [];
        if (seasons.length === 0) return "-";

        const years = seasons
          .map((s) => s.year)
          .filter((y): y is number => y != null)
          .sort((a, b) => a - b);

        if (years.length === 0) return "-";

        const start = years[0];
        const end = years[years.length - 1];

        if (start === end) return `${start}`;
        return `${start} - ${end}`;
      },
    },
    {
      title: "Sezony",
      key: "seasons",
      width: 80,
      align: "center",
      responsive: ["lg"],
      render: (_: unknown, record: Work) => {
        const count = record.series_details?.seasons?.length || 0;
        return count > 0 ? count : "-";
      },
    },
    {
      title: "Odcinki",
      key: "episodes",
      width: 100,
      align: "center",
      responsive: ["lg"],
      render: (_: unknown, record: Work) => {
        const seasons = record.series_details?.seasons || [];
        const total = seasons.reduce(
          (sum, s) => sum + (s.episodes_count || 0),
          0,
        );
        return total > 0 ? total : "-";
      },
    },
    {
      title: "Platforma",
      key: "platforms",
      width: 150,
      align: "center",
      responsive: ["lg"],
      render: (_: unknown, record: Work) => {
        const platform = record.series_details?.streaming_platform;
        return platform ? platform.name : "-";
      },
    },
    {
      title: "Akcje",
      key: "actions",
      width: 180,
      align: "center",
      render: (_: unknown, record: Work) => (
        <Space>
          <Tooltip title="Edytuj">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => navigate(`/series/${record.id}/edit`)}
            />
          </Tooltip>
          <Tooltip title="Dodaj sezon">
            <Button
              type="text"
              icon={<AppstoreAddOutlined style={{ color: "#1890ff" }} />}
              onClick={() => navigate(`/series/${record.id}/add-season`)}
            />
          </Tooltip>
          <Popconfirm
            title="Usunąć ten serial?"
            description="Tej operacji nie można cofnąć."
            onConfirm={() => deleteMutation.mutate(record.id)}
            okText="Usuń"
            cancelText="Anuluj"
            okButtonProps={{ danger: true }}
            disabled={!canDelete}
          >
            <Tooltip title={!canDelete ? "Brak uprawnień do usuwania" : "Usuń"}>
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                disabled={!canDelete}
              />
            </Tooltip>
          </Popconfirm>
          <Tooltip title="Dodaj do biblioteczki">
            <Button
              type="text"
              icon={<FolderAddOutlined style={{ color: "#52c41a" }} />}
              onClick={() =>
                navigate(`/my-library/add/series?work_id=${record.id}`)
              }
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <Title level={2} style={{ margin: 0 }}>
          <Tv size={20} style={{ marginRight: 12, color: "#faad14" }} />
          Seriale
        </Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate("/series/new")}
        >
          Dodaj serial
        </Button>
      </div>

      <Card>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={8}>
            <Search
              placeholder="Szukaj po tytule..."
              allowClear
              onSearch={setSearch}
              style={{ width: "100%" }}
            />
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={data?.items || []}
          rowKey="id"
          loading={isLoading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: data?.total || 0,
            showSizeChanger: true,
            pageSizeOptions: ["100", "200", "500"],
            showTotal: (total: number) => `Łącznie ${total} seriali`,
          }}
          onChange={handleTableChange}
        />
      </Card>

      {/* Custom Image Preview Modal */}
      <Modal
        open={previewOpen}
        footer={null}
        closable={false}
        width="auto"
        centered
        onCancel={closePreview}
        modalRender={(modal) => (
          <div
            style={{
              pointerEvents: "auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              maxWidth: "100vw",
              maxHeight: "100vh",
              outline: "none",
            }}
          >
            {modal}
          </div>
        )}
        style={{ top: 0, overflow: "hidden" }}
        bodyStyle={{
          padding: 0,
          backgroundColor: "transparent",
          boxShadow: "none",
        }}
        maskStyle={{ backgroundColor: "rgba(0, 0, 0, 0.85)" }}
      >
        <div
          style={{
            position: "relative",
            display: "inline-block",
            lineHeight: 0,
          }}
        >
          <img
            src={previewUrl}
            alt="Podgląd"
            style={{
              maxWidth: "90vw",
              maxHeight: "90vh",
              objectFit: "contain",
              cursor: "zoom-out",
              display: "block",
              borderRadius: 4,
            }}
            onClick={closePreview}
          />
          <div
            onClick={(e) => {
              e.stopPropagation();
              closePreview();
            }}
            style={{
              position: "absolute",
              top: -20,
              right: -20,
              width: 40,
              height: 40,
              borderRadius: "50%",
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              fontSize: 20,
              transition: "background 0.3s",
              zIndex: 10,
            }}
            className="preview-close-btn"
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor =
                "rgba(255, 255, 255, 0.4)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor =
                "rgba(255, 255, 255, 0.2)")
            }
          >
            <CloseOutlined />
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default SeriesListPage;
